import { useState, useMemo, useCallback } from "react";
import { sortByTitle, sortByDate } from "../../../utils/sorting";

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useMemo(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export function useGalleryFilters() {
    const [searchKeyword, setSearchKeyword] = useState("");
    const [mediaTypeFilter, setMediaTypeFilter] = useState("all");
    const [sortField, setSortField] = useState("none");
    const [sortMode, setSortMode] = useState("asc");

    const debouncedSearchKeyword = useDebounce(searchKeyword, 300);

    const filterMedia = useCallback((mediaWithLocation) => {
        // TODO: バックエンドでソート・フィルターを実装したら、このフロントエンドでのフィルタリング処理は削除する
        // バックエンドから既にフィルタリングされたデータを受け取るように変更する
        let filtered = mediaWithLocation;

        // メディアタイプでフィルタリング
        if (mediaTypeFilter !== "all") {
            filtered = filtered.filter(({ media }) => {
                return media?.media_type === mediaTypeFilter;
            });
        }

        // キーワード検索
        if (debouncedSearchKeyword.trim()) {
            const keyword = debouncedSearchKeyword.toLowerCase().trim();
            filtered = filtered.filter(({ media, location }) => {
                return (
                    location.title?.toLowerCase().includes(keyword) ||
                    (media.filename || media.name || "").toLowerCase().includes(keyword) ||
                    media.description?.toLowerCase().includes(keyword)
                );
            });
        }

        return filtered;
    }, [debouncedSearchKeyword, mediaTypeFilter]);

    const sortMedia = useCallback((filteredData) => {
        // TODO: バックエンドでソート・フィルターを実装したら、このフロントエンドでのソート処理は削除する
        // バックエンドから既にソートされたデータを受け取るように変更する
        if (sortField === "none") {
            return filteredData;
        }

        switch (sortField) {
            case "title":
                return sortByTitle(filteredData, sortMode);
            case "date":
                return sortByDate(filteredData, sortMode);
            default:
                return filteredData;
        }
    }, [sortField, sortMode]);

    const filterAndSortMedia = useCallback((mediaWithLocation) => {
        const filtered = filterMedia(mediaWithLocation);
        return sortMedia(filtered);
    }, [filterMedia, sortMedia]);

    const getSearchStats = useCallback((mediaWithLocation) => {
        const filtered = filterMedia(mediaWithLocation);
        return {
            total: mediaWithLocation.length,
            filtered: filtered.length,
            hasResults: filtered.length > 0,
            hasSearch: debouncedSearchKeyword.trim().length > 0 || mediaTypeFilter !== "all"
        };
    }, [filterMedia, debouncedSearchKeyword, mediaTypeFilter]);

    return {
        searchKeyword,
        setSearchKeyword,
        mediaTypeFilter,
        setMediaTypeFilter,

        sortField,
        setSortField,
        sortMode,
        setSortMode,

        filterAndSortMedia,
        getSearchStats
    };
} 